class HotspotModule extends HTMLElement {
  constructor() {
    super();
    this.initDomElements();
    this.bindEventHandlers();
    this.isTransitioning = false;
  }

  setTransitioningState(isTransitioning) {
    this.isTransitioning = isTransitioning;
    if (isTransitioning) {
      this.classList.add('is-transitioning');
    } else {
      this.classList.remove('is-transitioning');
    }
  }

  connectedCallback() {
    this.addEventListeners();
  }

  disconnectedCallback() {
    this.removeEventListeners();
  }

  initDomElements() {
    this.swiper = this.querySelector('.swiper');
    this.swiperWrapper = this.swiper?.querySelector('.swiper-wrapper');
    this.swiperSlides = Array.from(this.swiperWrapper?.querySelectorAll('.swiper-slide'));
    this.swiperSpeed = 1000;

    this.slides = Array.from(this.querySelectorAll('[data-slide]'));
    this.hotspots = Array.from(this.querySelectorAll('[data-hotspot]'));

    this.hotspotsWrapper = Array.from(this.querySelectorAll('[hotspot]'));
    this.hotspotsIndex = Array.from(this.querySelectorAll('[index] button[data-hotspot]'));
    this.thumbnails = Array.from(this.querySelectorAll('[thumbs] button'));

    this.copyButton = this.querySelector('[copy]');
    this.jsonData = this.querySelector('[json-data]');
    this.prevNextButtons = Array.from(this.querySelectorAll('[index] [data-direction]'));
  }

  bindEventHandlers() {
    this.handleSlideClickBound = this.handleSlideClick.bind(this);
    this.handleHotspotClickBound = this.handleHotspotClick.bind(this);
    this.handleDocumentClickBound = this.handleDocumentClick.bind(this);
    this.copyJsonBound = this.copyJson.bind(this);
    this.scrollToSlideBound = this.scrollToSlide.bind(this);
    this.prevNextBound = this.prevNext.bind(this);
  }

  addEventListeners() {
    this.slides.forEach((slide) => slide.addEventListener('click', this.handleSlideClickBound));
    this.hotspots.forEach((hotspot) => hotspot.addEventListener('click', this.handleHotspotClickBound));
    this.prevNextButtons.forEach((button) => button.addEventListener('click', this.prevNextBound));
    this.addEventListener('click', this.handleDocumentClickBound);

    if (window.Shopify?.designMode) {
      if (this.copyButton) {
        this.copyButton.addEventListener('click', this.copyJsonBound);
      }

      document.addEventListener('shopify:block:select', this.scrollToSlideBound);
    }
  }

  removeEventListeners() {
    this.slides.forEach((slide) => slide.removeEventListener('click', this.handleSlideClickBound));
    this.hotspots.forEach((hotspot) => hotspot.removeEventListener('click', this.handleHotspotClickBound));
    this.prevNextButtons.forEach((button) => button.removeEventListener('click', this.prevNextBound));
    this.removeEventListener('click', this.handleDocumentClickBound);

    if (window.Shopify?.designMode) {
      if (this.copyButton) {
        this.copyButton.removeEventListener('click', this.copyJsonBound);
      }

      document.removeEventListener('shopify:block:select', this.scrollToSlideBound);
    }
  }

  scrollToSlide(event) {
    const selectedBlockId = event.detail.blockId;
    const viewportWidth = this.swiperWrapper.clientWidth;

    this.swiperSlides.forEach((slide, index) => {
      if (slide.dataset.shopifyEditorBlock.includes(selectedBlockId)) {
        this.scrollToPosition(index * viewportWidth);
      }
    });
  }

  handleSlideClick(event) {
    if (event.currentTarget.classList.contains('active') && event.currentTarget.parentElement.hasAttribute('index')) {
      return;
    }

    const slideId = event.currentTarget.dataset.slide;

    if (window.Shopify?.designMode) {
      this.scrollToPosition(slideId * window.innerWidth);
      return;
    }

    if (this.swiper?.swiper) {
      if (this.isTransitioning) return;

      this.setTransitioningState(true);

      const activeHotspot = this.hotspotsWrapper.find((hotspot) => hotspot.classList.contains('active'));
      const currentSlideId = this.swiper.swiper.activeIndex;

      const delay = activeHotspot ? this.swiperSpeed : 1;
      setTimeout(() => {
        if (currentSlideId !== parseInt(slideId, 10)) {
          this.animateHotspotTransition(slideId);
        } else {
          this.setTransitioningState(false);
        }
        this.swiper.swiper.slideTo(slideId);
        this.updateThumbnails(slideId);
      }, delay);
    }
  }

  animateHotspotTransition(slideId) {
    // Get the active slide and the target slide
    const activeSlide = this.swiperSlides[this.swiper.swiper.activeIndex];
    const targetSlide = this.swiperSlides[slideId];

    // Get hotspots from the active slide and the target slide
    const activeHotspots = Array.from(activeSlide.querySelectorAll('[hotspot]'));
    const targetHotspots = Array.from(targetSlide.querySelectorAll('[hotspot]'));

    // Duplicate the active hotspots
    const temporaryHotspots = activeHotspots.map((hotspot) => {
      const clone = hotspot.cloneNode(true);
      clone.classList.add('temporary');
      this.swiper.appendChild(clone);
      return clone;
    });

    // Hide the original hotspots on the active slide
    this.hotspotsWrapper.forEach((hotspot) => {
      hotspot.style.transition = 'opacity 0s';
      hotspot.style.opacity = '0';
    });

    // Animate the temporary hotspots to the new slide
    const isMobile = window.innerWidth <= 1023;

    temporaryHotspots.forEach((tempHotspot, index) => {
      const xVar = isMobile ? '--position-x-mobile' : '--position-x-desktop';
      const yVar = isMobile ? '--position-y-mobile' : '--position-y-desktop';

      const targetHotspot = targetHotspots[index];
      if (targetHotspot) {
        const targetX = getComputedStyle(targetHotspot).getPropertyValue(xVar);
        const targetY = getComputedStyle(targetHotspot).getPropertyValue(yVar);

        tempHotspot.style.setProperty(xVar, targetX);
        tempHotspot.style.setProperty(yVar, targetY);
      } else {
        // If there are no corresponding target hotspots, fade out the extra temporary hotspots
        tempHotspot.style.opacity = '0';
      }
    });

    // After the animation
    setTimeout(() => {
      // Show the original hotspots
      this.hotspotsWrapper.forEach((hotspot) => {
        hotspot.style.transition = '';
        hotspot.style.opacity = '';
      });

      // Remove the temporary hotspots
      setTimeout(() => {
        temporaryHotspots.forEach((tempHotspot) => tempHotspot.remove());
      }, this.swiperSpeed);

      this.setTransitioningState(false);
    }, this.swiperSpeed);
  }

  scrollToPosition(position) {
    this.swiperWrapper.scrollTo({
      left: position,
      behavior: 'instant',
    });
  }

  updateThumbnails(slideId) {
    this.thumbnails.forEach((thumb) => {
      thumb.classList.toggle('active', thumb.dataset.slide === slideId);
    });
  }

  handleHotspotClick(event) {
    event.stopPropagation();

    if (event.currentTarget.classList.contains('active') && event.currentTarget.parentElement.hasAttribute('index')) {
      return;
    }

    const hotspotId = event.currentTarget.dataset.hotspot;
    this.highlightHotspot(hotspotId);
    this.openHotspotEditor(hotspotId);
  }

  prevNext(event) {
    event.stopPropagation();
    const direction = event.currentTarget.dataset.direction;
    const currentIndex = this.hotspotsIndex.findIndex((hotspot) => hotspot.classList.contains('active'));
    let newIndex;

    if (direction === 'previous') {
      newIndex = currentIndex - 1 < 0 ? this.hotspotsIndex.length - 1 : currentIndex - 1;
    } else {
      newIndex = currentIndex + 1 >= this.hotspotsIndex.length ? 0 : currentIndex + 1;
    }

    this.hotspotsIndex[newIndex].click();
  }

  highlightHotspot(hotspotId) {
    this.toggleActiveClass(this.hotspotsIndex, (hotspot) => hotspot.dataset.hotspot === hotspotId);
  }

  openHotspotEditor(hotspotId) {
    const activeHotspot = this.hotspotsWrapper.find((hotspot) => hotspot.classList.contains('active'));
    const currentSlideId = this.thumbnails.findIndex((thumb) => thumb.classList.contains('active'));

    const newHotspot = this.hotspotsWrapper.find((hotspot) => hotspot.id === hotspotId);
    const slideId = parseInt(newHotspot.dataset.slideIndex, 10);

    let delay = this.swiperSpeed;

    if (activeHotspot) {
      activeHotspot.classList.remove('active');

      if (currentSlideId !== slideId && !window.Shopify?.designMode) {
        delay = this.swiperSpeed * 2 + 1000;
      }
    } else {
      if (currentSlideId !== slideId && !window.Shopify?.designMode) {
        delay = this.swiperSpeed + 1000;
      } else {
        delay = 0;
      }
    }

    setTimeout(() => {
      this.toggleActiveClass(this.hotspotsWrapper, (hotspot) => hotspot.id === hotspotId);
    }, delay);
  }

  toggleActiveClass(elements, condition) {
    elements.forEach((element) => {
      element.classList.toggle('active', condition(element));
    });
  }

  handleDocumentClick(event) {
    if (!event.target.closest('[data-hotspot]')) {
      this.deactivateAllHotspots();
    }
  }

  deactivateAllHotspots() {
    this.toggleActiveClass(this.hotspotsWrapper, () => false);
    this.toggleActiveClass(this.hotspotsIndex, () => false);
  }

  copyJson() {
    if (!this.jsonData) return;

    const textarea = document.createElement('textarea');
    textarea.value = this.jsonData.innerHTML;
    document.body.appendChild(textarea);
    textarea.select();

    try {
      document.execCommand('copy');
      alert('JSON data copied to clipboard');
    } catch (error) {
      alert(`Error copying JSON data: ${error}`);
    } finally {
      document.body.removeChild(textarea);
    }
  }
}

customElements.define('hotspot-module', HotspotModule);
